import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useRefcodeLookup } from "../hooks/useRefcodeLookup";
import { WalletLink } from "../components/WalletLink";
import { Skeleton } from "../components/Skeleton";

export function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const refcodeFromUrl = searchParams.get("refcode");

  const [searchType, setSearchType] = useState<"wallet" | "refcode">("refcode");
  const [query, setQuery] = useState(refcodeFromUrl || "");

  // Auto-trigger search if refcode is in URL
  const { data, isLoading, error } = useRefcodeLookup(
    searchType === "refcode" && query ? query : null
  );

  // Update query when URL changes
  useEffect(() => {
    if (refcodeFromUrl) {
      setQuery(refcodeFromUrl);
      setSearchType("refcode");
    }
  }, [refcodeFromUrl]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    if (searchType === "wallet") {
      navigate(`/wallet/${trimmed}`);
    }
    // For refcode, the useRefcodeLookup hook will handle it
  };

  // Auto-detect input type
  const handleQueryChange = (value: string) => {
    setQuery(value);
    // If it looks like a Solana address, switch to wallet search
    if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(value)) {
      setSearchType("wallet");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Search</h1>
        <p className="section-hint mt-1">
          Look up a wallet by address or find the wallet behind a referral code.
        </p>
      </div>

      {/* Search Form */}
      <section className="card">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Search type toggle */}
          <div className="seg">
            <button
              type="button"
              onClick={() => setSearchType("refcode")}
              className={`seg-btn ${searchType === "refcode" ? "seg-btn-on" : ""}`}
            >
              Referral code
            </button>
            <button
              type="button"
              onClick={() => setSearchType("wallet")}
              className={`seg-btn ${searchType === "wallet" ? "seg-btn-on" : ""}`}
            >
              Wallet address
            </button>
          </div>

          {/* Search input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder={
                searchType === "wallet"
                  ? "Wallet address"
                  : "Referral code"
              }
              className="input flex-1"
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </div>
        </form>
      </section>

      {/* Search Results */}
      {searchType === "refcode" && query && (
        <section className="card">
          <h2 className="section-title mb-4">Results</h2>

          {isLoading && <Skeleton kind="lines" rows={3} />}

          {error && (
            <div>
              <p className="text-red-400">No wallet has the refcode "{query}".</p>
              <p className="text-gray-500 text-sm mt-2">
                Check the spelling. Wallets are indexed after their first win, so a brand-new
                refcode can take a little while to appear. You can also paste the wallet address
                or a Solscan link instead.
              </p>
            </div>
          )}

          {data?.data && (
            <div className="space-y-4">
              <div className="card-inset p-4">
                <div className="text-gray-400 text-sm mb-1">
                  Wallet with refcode "{query}"
                </div>
                <div className="flex items-center gap-3">
                  <WalletLink
                    address={data.data.walletAddress}
                    truncate={false}
                    showCopy
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <Link
                  to={`/wallet/${data.data.walletAddress}`}
                  className="btn btn-secondary"
                >
                  Open wallet
                </Link>
              </div>

              {/* Quick stats */}
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div className="card-inset p-3">
                  <div className="text-gray-400 text-sm">Referred wallets</div>
                  <div className="text-2xl font-semibold text-gray-50 num">
                    {data.data.referredWallets?.length ?? 0}
                  </div>
                </div>
                {data.data.referredBy && (
                  <div className="card-inset p-3">
                    <div className="text-gray-400 text-sm">Referred by</div>
                    <div className="text-lg font-mono text-gray-100">
                      {data.data.referredBy}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
